import { IconButton, Grid, Typography, Box, styled } from '@mui/material'
import IncreaseIcon from '@mui/icons-material/Add'
import DecreaseIcon from '@mui/icons-material/Remove'
import Button from '@mui/material/Button'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import useSplitPaymentContext from './SplitPaymentContext'
import { PaymentTypeInput } from './PaymentTypeInput'
import styles from '../../paymentDialog.style'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { isUndefined } from 'lodash-es'
import { useFormat } from 'Services/localize/format'
import Decimal from 'decimal.js'
import Warning from '@mui/icons-material/Warning'
import { sumDecimal } from 'utilities/decimalsManipulate'
import usePaymentContext from '../../hook/usePaymentContext'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'

const CenterBox = styled(Box)({
    display: 'grid',
    justifyItems: 'center',
})

const AmountBox = styled(Box)(({ theme }) => ({
    width: 200,
    height: 50,
    fontSize: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #dbdbdb',
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
        width: 100,
    },
}))

const ModifyButtonContainer = styled(Grid)({
    paddingTop: 10,
    paddingBottom: 30,
})

const ModifyButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.mode === 'light' ? '#fff' : undefined,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
    maxHeight: 48,
    '&:disabled': {
        backgroundColor: theme.palette.disabled.main,
        color: theme.palette.disabled.contrastText,
    },
}))

export function PaymentsForm() {
    const { t } = useTranslation()
    const {
        paymentCount,
        reducePayment,
        deletePayment,
        addPayment,
        onChangePayment,
        remainingAmount,
        splitEvenly,
    } = useSplitPaymentContext()
    const { paymentForms } = usePaymentContext()
    const ableToRemove = useMemo(() => {
        const paids = paymentForms.filter((a) => !isUndefined(a.PaymentID))
        if (paids.length > 0) return paymentForms.length - paids.length > 1
        else return paymentForms.length > 2
    }, [paymentForms])
    const { currency } = useFormat()
    const valueIsNotAllowed = useMemo(() => {
        const diff = remainingAmount.minus(
            new Decimal(
                sumDecimal(
                    (paymentForms.filter((i) => !i.PaymentID) ?? []).flatMap(
                        (i) => i.expectAmount,
                    ),
                ),
            ),
        )
        if (diff.equals(new Decimal(0))) {
            return undefined
        } else {
            return ` ${currency(diff.abs().toNumber())} ${t(
                diff.lessThan(new Decimal(0))
                    ? 'finish.warning.greater_than'
                    : 'finish.warning.less_than',
            )} ${currency(remainingAmount.toNumber())}`
        }
    }, [remainingAmount, paymentForms, t, currency])
    const { isMobile } = useScreenSize()
    return (
        <>
            <Box py={3}>
                <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                >
                    <ModifyButtonContainer item xs="auto">
                        <ModifyButton
                            disabled={!ableToRemove}
                            onClick={reducePayment}
                            size="large"
                        >
                            <DecreaseIcon />
                        </ModifyButton>
                    </ModifyButtonContainer>
                    <Grid item xs="auto" padding={1}>
                        <CenterBox>
                            <AmountBox>{paymentCount}</AmountBox>

                            <Typography color={'secondary'} variant="subtitle2">
                                {t('financials.title.payments')}
                            </Typography>
                        </CenterBox>
                    </Grid>
                    <ModifyButtonContainer item xs="auto">
                        <ModifyButton onClick={addPayment} size="large">
                            <IncreaseIcon />
                        </ModifyButton>
                    </ModifyButtonContainer>
                </Grid>
            </Box>
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={2}
            >
                {valueIsNotAllowed && (
                    <Grid item maxWidth={'80vw'}>
                        <Box
                            maxWidth={'80vw'}
                            sx={(t) => ({
                                px: 1,
                                background: t.palette.warning.main,
                                color: t.palette.warning.contrastText,
                                borderRadius: 8,
                            })}
                        >
                            <Typography textAlign={'center'}>
                                <Warning fontSize="small" />
                                {t('finish.warning.cannot_make_charge')} :
                                <Typography fontWeight={600} component={'span'}>
                                    {valueIsNotAllowed}
                                </Typography>
                            </Typography>
                        </Box>
                    </Grid>
                )}
                {paymentForms.map((p, i) => {
                    return (
                        <Grid item key={`split-payment-form-${i}`}>
                            <PaymentTypeInput
                                disableRemove={!ableToRemove}
                                payment={p}
                                index={i}
                                onUpdatePayment={(payment) =>
                                    onChangePayment(payment, i)
                                }
                                onDelete={() => deletePayment(i)}
                                disableCharge={
                                    !isUndefined(p.PaymentID) ||
                                    p.expectAmount.equals(0) ||
                                    Boolean(valueIsNotAllowed)
                                }
                            />
                        </Grid>
                    )
                })}
            </Grid>
            {!isMobile && (
                <Box
                    width={'100%'}
                    p={1}
                    sx={
                        paymentForms.length > 7
                            ? styles.splitEvenlyButtonContainer
                            : undefined
                    }
                >
                    <Button
                        fullWidth={isMobile}
                        disabled={paymentForms.some((a) => a.giftcard)}
                        variant="outlined"
                        color="primary"
                        sx={isMobile ? undefined : styles.splitEvenlyButton}
                        onClick={splitEvenly}
                    >
                        {t('finish.button.split_evenly')}
                    </Button>

                    <CenterBox>
                        <Box p={3} sx={{ display: 'inline-flex' }}>
                            <Typography sx={{ paddingRight: 2 }} variant={'h5'}>
                                {t('finish.label.remaining_amount')}:{' '}
                            </Typography>
                            <MonetaryFormat
                                variant={'h5'}
                                value={remainingAmount.toNumber()}
                            />
                        </Box>
                    </CenterBox>
                </Box>
            )}
        </>
    )
}
